import type { ReactNode } from "react";

import { useCallback, useMemo } from "react";

import type { DateValueType } from "../../../DatepickerV2";
import type { DropdownOption } from "../../../DropDownV2";
import type { MultiSelectOption } from "../../../MultiSelect";
import type {
  ClientsPageAnalytics,
  ReturnedTranslatedContentClientsPageHook,
} from "../../types";
import type { ClientTradingTableFilterSubProps } from "./types";

import { useIsMobile } from "../../../hooks";
import { useDebounceFn } from "../../../utils/debounce";
import { formatClientTrackingDate } from "../../utils";
import { PII_FIELDS } from "../consts";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";
import { DesktopFilters } from "./DesktopFilters";
import { MobileFilters } from "./MobileFilters";
import { MobileFiltersV2 } from "./MobileFiltersV2";

export type ClientTradingReportFilterProps = {
  analytics?: ClientsPageAnalytics;
  translations: ReturnedTranslatedContentClientsPageHook;
  isMobileBottomSheetEnable?: boolean;
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showRebateProgramFilter?: boolean;
  /** Gated by the part-4039-master-sub-ib-phase-2 flag -- see
   * `ClientTradingTableFilterSubProps.showToolbarDateRange`. */
  showToolbarDateRange?: boolean;
  showClientTypeFilter?: boolean;
  subIBsLoadFailed?: boolean;
  onRetrySubIBs?: () => void;
  toolbarLeading?: ReactNode;
};

export const ClientTradingReportFilters = ({
  analytics,
  translations,
  isMobileBottomSheetEnable,
  showRebateProgramFilter,
  showToolbarDateRange,
  showClientTypeFilter,
  subIBsLoadFailed,
  onRetrySubIBs,
  toolbarLeading,
}: ClientTradingReportFilterProps) => {
  const isMobile = useIsMobile();

  // No default rebate account selection -- until the user picks one via the
  // Filter modal, the report shows the full IB data for the date range.
  const {
    state: { dateRange, selected },
    resources: {
      table: { data: tableData },
    },
    actions,
    runResource,
  } = useClientTradingReportContext();

  const canViewPII = !PII_FIELDS.every((field) =>
    tableData?.redactedFields?.includes(field),
  );

  const [handleSearchInput] = useDebounceFn((value: string) => {
    actions.setSearchQuery(value);
    analytics?.trackTableSearchFieldChangeEvent("Trading report", value);
  }, 1000);

  const handleDateRangePicker = useCallback(
    (value: DateValueType) => {
      if (
        formatClientTrackingDate(value?.startDate) ===
          formatClientTrackingDate(dateRange.from) ||
        formatClientTrackingDate(value?.endDate) ===
          formatClientTrackingDate(dateRange.to)
      ) {
        analytics?.trackDateRangeFilterEvent(
          formatClientTrackingDate(value?.startDate),
          formatClientTrackingDate(value?.endDate),
          "Trading report",
        );
      }

      actions.setDateRange({
        from: value?.startDate ?? null,
        to: value?.endDate ?? null,
      });
    },
    [actions, analytics, dateRange.from, dateRange.to],
  );

  const handleRebateAccountsDropdown = useCallback(
    (options: DropdownOption[]) => {
      const newRebateAccountIDs = options.map((o) => o.value);
      if (
        JSON.stringify(newRebateAccountIDs) !==
        JSON.stringify(selected.rebateAccountIDs)
      ) {
        analytics?.trackTableRebateSelectEventV2(
          "Trading report",
          newRebateAccountIDs,
        );
      }

      actions.setSelected("rebateAccountIDs", newRebateAccountIDs);
    },
    [actions, analytics, selected.rebateAccountIDs],
  );

  const handleRebateProgramsMultiSelect = useCallback(
    (options: MultiSelectOption[]) => {
      actions.setSelected(
        "rebateProgramIDs",
        options.map((o) => o.value),
      );
    },
    [actions],
  );

  const [debounceDateTooltipTrack] = useDebounceFn((isMobile: boolean) => {
    analytics?.trackClientDateTooltipEvent(isMobile, "Trading report");
  }, 5000);

  const filterConfig = useMemo<ClientTradingTableFilterSubProps>(
    () => ({
      handleDateRangePicker,
      handleExportButton: () => runResource("csv"),
      handleRebateAccountsDropdown,
      handleRebateProgramsMultiSelect,
      handleSearchInput,
      canViewPII,
      redactedFields: tableData?.redactedFields,
      translations,
      onDateTooltipHover: debounceDateTooltipTrack,
      showRebateProgramFilter,
      showToolbarDateRange,
      showClientTypeFilter,
      subIBsLoadFailed,
      onRetrySubIBs,
      toolbarLeading,
    }),
    [
      handleDateRangePicker,
      handleRebateAccountsDropdown,
      handleRebateProgramsMultiSelect,
      handleSearchInput,
      canViewPII,
      tableData?.redactedFields,
      translations,
      debounceDateTooltipTrack,
      runResource,
      showRebateProgramFilter,
      showToolbarDateRange,
      showClientTypeFilter,
      subIBsLoadFailed,
      onRetrySubIBs,
      toolbarLeading,
    ],
  );

  if (isMobile && isMobileBottomSheetEnable) {
    return (
      <>
        {toolbarLeading && <div className="mt-10">{toolbarLeading}</div>}
        <MobileFiltersV2
          hasToolbarLeading={Boolean(toolbarLeading)}
          handleSearchInput={handleSearchInput}
          canViewPII={canViewPII}
          redactedFields={tableData?.redactedFields}
          showRebateProgramFilter={showRebateProgramFilter}
          showToolbarDateRange={showToolbarDateRange}
          showClientTypeFilter={showClientTypeFilter}
          subIBsLoadFailed={subIBsLoadFailed}
          onRetrySubIBs={onRetrySubIBs}
        />
      </>
    );
  }

  return isMobile ? (
    <>
      {toolbarLeading && <div className="mt-10">{toolbarLeading}</div>}
      <MobileFilters {...filterConfig} />
    </>
  ) : (
    <DesktopFilters {...filterConfig} />
  );
};
